import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Col, Row, Button, Modal, QRCode } from "antd";
import axios from "axios";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { RiAlignItemBottomFill, RiQrCodeFill } from "react-icons/ri";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  booleanOptions,
  plateAccessTypeOptions,
  plateDirectoryModeOptions,
} from "../../../utils/Common";
import {
  getDealerNo,
  getOemNo,
  hasRole,
} from "../../../global/hooks/getUserRole";
import {
  PlateMapPicker,
  resolvePlateMapLocation,
  roundCoord,
} from "./PlateMapPicker";

/** Per-session cache for /api/plates/site-control payload by DeviceNo. */
const siteControlCacheByDevice = new Map();

function trimLeadingSlashes(s) {
  return String(s || "").replace(/^\/+/, "");
}

/**
 * Cloud site types (site-control / device list): numeric 3 or 4, or label containing "cloud".
 */
function isCloudSiteType(siteTypeValue) {
  if (siteTypeValue == null || siteTypeValue === "") return false;
  const numeric = Number(siteTypeValue);
  if (Number.isFinite(numeric)) {
    return numeric === 3 || numeric === 4;
  }
  const text = String(siteTypeValue).toLowerCase();
  return text.includes("cloud");
}

/** Path on the device Neptune host (no leading slash). Override with REACT_APP_NEPTUNE_ACCESS_OPTIONS_PATH. */
const NEPTUNE_ACCESS_OPTIONS_PATH = trimLeadingSlashes(
  process.env.REACT_APP_NEPTUNE_ACCESS_OPTIONS_PATH || "api/devices/options",
);

const NEPTUNE_OPTIONS_MAX_RETRIES = Math.max(
  1,
  Math.min(
    10,
    Number(process.env.REACT_APP_NEPTUNE_OPTIONS_MAX_RETRIES || 5) || 5,
  ),
);
const NEPTUNE_RETRY_BASE_DELAY_S = 0.3;
const NEPTUNE_RETRY_MAX_DELAY_S = 1.5;

function sleepSeconds(seconds) {
  const ms = Math.max(0, seconds) * 1000;
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * POST to Neptune with retries (same spirit as backend _request_with_retry):
 * - Retries on network failure / timeout and on 5xx.
 * - No retry on 4xx.
 */
async function postNeptuneDevicesOptionsWithRetry(url, body, headers) {
  let lastNetworkError = null;
  for (let attempt = 0; attempt < NEPTUNE_OPTIONS_MAX_RETRIES; attempt++) {
    try {
      const res = await axios.post(url, body, {
        headers,
        timeout: 25000,
        validateStatus: () => true,
      });

      if (res.status >= 500 && res.status < 600) {
        if (attempt < NEPTUNE_OPTIONS_MAX_RETRIES - 1) {
          await sleepSeconds(NEPTUNE_RETRY_BASE_DELAY_S);
          continue;
        }
        return { ok: false, response: res, error: null };
      }

      return { ok: true, response: res };
    } catch (err) {
      lastNetworkError = err;
      if (attempt === NEPTUNE_OPTIONS_MAX_RETRIES - 1) {
        return { ok: false, response: null, error: err };
      }
      const delay =
        Math.min(
          NEPTUNE_RETRY_MAX_DELAY_S,
          NEPTUNE_RETRY_BASE_DELAY_S * 2 ** attempt,
        ) +
        Math.random() * 0.2;
      await sleepSeconds(delay);
    }
  }
  return { ok: false, response: null, error: lastNetworkError };
}

/**
 * Single Neptune URL for access-item options (matches backend rules).
 * Cloud (SiteType 3 or 4): https://{proxy}{/path}
 * Non-cloud: https://{proxy}:{port}{/path} when port > 0, else https://{proxy}{/path}
 */
function buildNeptuneAccessOptionsUrl(proxyAddr, securePort, isCloud) {
  const host = proxyAddr != null ? String(proxyAddr).trim() : "";
  if (!host) return null;
  const pathSuffix = `/${NEPTUNE_ACCESS_OPTIONS_PATH}`;
  if (isCloud) {
    return `https://${host}${pathSuffix}`;
  }
  const portNum =
    securePort != null && securePort !== "" ? Number(securePort) : NaN;
  if (Number.isFinite(portNum) && portNum > 0) {
    return `https://${host}:${portNum}${pathSuffix}`;
  }
  return `https://${host}${pathSuffix}`;
}

export const PlatesForm = ({ type }) => {
  /** Dealer login: no OEM/Dealer pickers; scope from session (same pattern as Purchase). */
  const hideOemDealerForDealerAccount = hasRole(7, 8);
  const hideOemForOemAccount = hasRole(4, 5, 6);
  const [showModal, setShowModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showAccessItemModal, setShowAccessItemModal] = useState(false);
  const [accessItemModalOptions, setAccessItemModalOptions] = useState([]);
  const [accessItemModalOptionsLoading, setAccessItemModalOptionsLoading] =
    useState(false);
  const [selectedOemNo, setSelectedOemNo] = useState(null);
  const [selectedDealerNo, setSelectedDealerNo] = useState(null);
  const [form] = Form.useForm();
  const watchedDeviceNo = Form.useWatch("DeviceNo", form);
  const watchedLocation = Form.useWatch("Location", form);
  const watchedPlateUrl = Form.useWatch("PlateUrl", form);
  const locationEnabled = Number(watchedLocation) === 1;
  const [mapPickerInitial, setMapPickerInitial] = useState({ lat: 0, lng: 0 });
  const [mapPickerCoords, setMapPickerCoords] = useState({ lat: 0, lng: 0 });
  const hasDeviceForAccessItem =
    watchedDeviceNo != null &&
    watchedDeviceNo !== "" &&
    !(typeof watchedDeviceNo === "number" && !Number.isFinite(watchedDeviceNo));
  const [accessItemModalForm] = Form.useForm();
  const accessTypeBeforeAccessItemModalRef = useRef(undefined);
  const { id } = useParams();
  const navigate = useNavigate();
  const url = id && `${process.env.REACT_APP_API_URL}/api/plates/${id}/`;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/plates/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/plates/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/plates/${id}/`;
  const accountOptionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;

  const { data, loading, refetch } = useGet(url);
  const { data: accountOptionsData } = useGet(accountOptionUrl);

  const resolveOemNo = useCallback(
    (value) => {
      if (value == null || value === "") return null;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
      const oemOption = accountOptionsData?.data?.oems?.find(
        (oem) => String(oem?.name) === String(value),
      );
      return oemOption?.value ?? null;
    },
    [accountOptionsData],
  );

  const resolvedOemNo =
    resolveOemNo(selectedOemNo) ??
    resolveOemNo(data?.data?.OemNo) ??
    (hideOemDealerForDealerAccount || hideOemForOemAccount
      ? resolveOemNo(getOemNo())
      : null);

  const dealerOptionsUrl =
    resolvedOemNo != null
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${resolvedOemNo}/dealers/`
      : null;
  const { data: dealerOptionsData } = useGet(dealerOptionsUrl);

  const getSessionDealerNoNum = () => {
    const d = getDealerNo();
    if (d == null || d === "") return null;
    const n = Number(d);
    return Number.isFinite(n) ? n : null;
  };

  const dealerNoFromForm = () => {
    const v = form.getFieldValue("DealerNo");
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const effectiveDealerNo = hideOemDealerForDealerAccount
    ? getSessionDealerNoNum()
    : selectedDealerNo != null
      ? selectedDealerNo
      : dealerNoFromForm();
  const deviceOptionsUrl =
    resolvedOemNo != null && effectiveDealerNo != null
      ? `${process.env.REACT_APP_API_URL}/api/plates/devices-list/?${new URLSearchParams(
          {
            OemNo: String(resolvedOemNo),
            DealerNo: String(effectiveDealerNo),
            limit: "1000",
          },
        ).toString()}`
      : null;
  const { data: deviceOptionsData } = useGet(deviceOptionsUrl);

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);

  const modifiedOems = useMemo(
    () => getModifiedResponse(accountOptionsData, "oems"),
    [accountOptionsData],
  );
  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptionsData, null),
    [dealerOptionsData],
  );
  const modifiedDevices = useMemo(() => {
    const devices = deviceOptionsData?.data || [];
    if (!Array.isArray(devices)) return [];
    return devices;
  }, [deviceOptionsData]);

  const onFinish = async (values) => {
    const plateCodeTrimmed =
      values?.PlateCode != null && String(values.PlateCode).trim() !== ""
        ? String(values.PlateCode).trim()
        : "";

    const { OemNo: _formOem, PlateCode: _code, ...rest } = values;
    const plateCodePayload =
      plateCodeTrimmed !== ""
        ? plateCodeTrimmed
        : id && data?.data
          ? String(data.data.PlateCode ?? "").trim()
          : "";

    const locationValue =
      values.Location != null && values.Location !== ""
        ? Number(values.Location)
        : 0;
    const directoryModeValue =
      values.DirectoryMode != null && values.DirectoryMode !== ""
        ? Number(values.DirectoryMode)
        : undefined;

    const payload = {
      ...rest,
      ...(hideOemDealerForDealerAccount
        ? {
            OemNo: getOemNo(),
            DealerNo: getDealerNo(),
          }
        : hideOemForOemAccount
          ? { OemNo: getOemNo() }
          : {}),
      PlateCode: plateCodePayload,
      DirectoryMode: directoryModeValue,
      Location: locationValue,
      Latitude:
        locationValue === 1 && values.Latitude != null && values.Latitude !== ""
          ? Number(values.Latitude)
          : 0,
      Longitude:
        locationValue === 1 &&
        values.Longitude != null &&
        values.Longitude !== ""
          ? Number(values.Longitude)
          : 0,
    };
    if (!hideOemDealerForDealerAccount && !hideOemForOemAccount) {
      delete payload.OemNo;
    }

    try {
      if (id) {
        await putAction(payload);
      } else {
        await postAction(payload);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  const onReset = () => {
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  useEffect(() => {
    if (!id || loading) return;
    try {
      form.resetFields();
      if (data?.data) {
        form.setFieldsValue({
          PlateName: data?.data?.PlateName ? String(data.data.PlateName) : "",
          PlateDesc: data?.data?.PlateDesc ? String(data.data.PlateDesc) : "",
          PlateCode:
            data?.data?.PlateCode != null && data.data.PlateCode !== ""
              ? String(data.data.PlateCode)
              : "",
          PlateUrl:
            data?.data?.PlateUrl != null && data.data.PlateUrl !== ""
              ? String(data.data.PlateUrl)
              : "",
          OemNo:
            data?.data?.OemNo || data?.data?.OemNo === 0
              ? Number(data.data.OemNo)
              : undefined,
          DealerNo:
            data?.data?.DealerNo || data?.data?.DealerNo === 0
              ? Number(data.data.DealerNo)
              : undefined,
          DeviceNo:
            data?.data?.DeviceNo || data?.data?.DeviceNo === 0
              ? Number(data.data.DeviceNo)
              : undefined,
          AccessType:
            data?.data?.AccessType !== undefined &&
            data?.data?.AccessType !== null
              ? Number(data.data.AccessType)
              : undefined,
          ItemName:
            data?.data?.ItemName !== undefined && data?.data?.ItemName !== null
              ? String(data.data.ItemName)
              : "",
          ItemNo:
            data?.data?.ItemNo !== undefined && data?.data?.ItemNo !== null
              ? String(data.data.ItemNo)
              : "",
          DirectoryMode:
            data?.data?.DirectoryMode !== undefined &&
            data?.data?.DirectoryMode !== null
              ? Number(data.data.DirectoryMode)
              : 1,
          Location:
            data?.data?.Location !== undefined && data?.data?.Location !== null
              ? Number(data.data.Location)
              : 0,
          Latitude:
            data?.data?.Latitude !== undefined && data?.data?.Latitude !== null
              ? Number(data.data.Latitude)
              : 0,
          Longitude:
            data?.data?.Longitude !== undefined &&
            data?.data?.Longitude !== null
              ? Number(data.data.Longitude)
              : 0,
        });
        setSelectedOemNo(data?.data?.OemNo || null);
        setSelectedDealerNo(data?.data?.DealerNo || null);
      }
    } catch (error) {
      if (!loading) {
        openErrorNotification("Error fetching data!");
      }
    }
  }, [id, data, loading, form, type]);

  useEffect(() => {
    if (!hideOemDealerForDealerAccount || id) return;
    setSelectedOemNo(resolveOemNo(getOemNo()));
    setSelectedDealerNo(getSessionDealerNoNum());
  }, [hideOemDealerForDealerAccount, id, resolveOemNo]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData, postLoading]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");
      refetch();
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [putData, putLoading, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/virtual-qr");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "Virtual QR/NFC",
      href: "/virtual-qr",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/virtual-qr/info/${id}`
          : `/virtual-qr/edit/${id}`
        : "/virtual-qr/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? hasRole(4, 5)
        ? [
            {
              label: "List",
              icon: <UnorderedListOutlined />,
              onClick: () => {
                navigate(-1);
              },
            },
          ]
        : [
            {
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => {
                navigate(`/virtual-qr/edit/${id}`);
              },
            },
            {
              label: "Delete",
              icon: <DeleteOutlined />,
              onClick: () => {
                setShowModal(true);
              },
            },
            {
              label: "List",
              icon: <UnorderedListOutlined />,
              onClick: () => {
                navigate(-1);
              },
            },
          ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  /**
   * Loads door / lockset / contlock options from Neptune (not Django):
   * POST /api/plates/site-control (proxy / site fields) → Neptune devices/options.
   */
  const fetchAccessItemOptions = useCallback(
    async (accessType, deviceNo) => {
      const at = Number(accessType);
      if (!Number.isFinite(at)) {
        setAccessItemModalOptions([]);
        return [];
      }
      const dn = deviceNo != null && deviceNo !== "" ? Number(deviceNo) : NaN;
      if (!Number.isFinite(dn)) {
        openErrorNotification("Please select a device first.");
        setAccessItemModalOptions([]);
        return [];
      }

      const devices = deviceOptionsData?.data;
      const device = Array.isArray(devices)
        ? devices.find((d) => Number(d?.DeviceNo) === dn)
        : null;

      setAccessItemModalOptionsLoading(true);
      try {
        let accessToken = null;
        try {
          const raw = sessionStorage.getItem("userData");
          const token = raw ? JSON.parse(raw) : null;
          accessToken = token?.access_token ?? null;
        } catch (tokenErr) {
          console.warn("Unable to parse userData token:", tokenErr);
        }

        const siteControlCacheKey = String(dn);
        let siteControlPayload =
          siteControlCacheByDevice.get(siteControlCacheKey);
        if (!siteControlPayload) {
          const siteControlUrl = `${process.env.REACT_APP_API_URL}/api/plates/site-control/`;
          const siteKey =
            device?.SiteKey ??
            device?.site_key ??
            data?.data?.SiteKey ??
            data?.data?.site_key;
          try {
            const scRes = await axios.post(
              siteControlUrl,
              {
                DeviceNo: dn,
                ...(siteKey ? { SiteKey: siteKey } : {}),
              },
              {
                headers: {
                  "Content-Type": "application/json; charset=utf-8",
                  ...(accessToken
                    ? { Authorization: `Bearer ${accessToken}` }
                    : {}),
                },
                timeout: 12000,
                validateStatus: () => true,
              },
            );
            if (scRes.status < 200 || scRes.status >= 300) {
              openErrorNotification("Failed to load access items.");
              setAccessItemModalOptions([]);
              return [];
            }
            siteControlPayload =
              scRes?.data?.data ??
              (scRes?.data != null && typeof scRes.data === "object"
                ? scRes.data
                : null);
            siteControlCacheByDevice.set(
              siteControlCacheKey,
              siteControlPayload || {},
            );
          } catch (scErr) {
            console.error("site-control failed:", scErr);
            openErrorNotification("Failed to load access items.");
            setAccessItemModalOptions([]);
            return [];
          }
        }

        const sc =
          siteControlPayload && typeof siteControlPayload === "object"
            ? siteControlPayload
            : {};
        const deviceLive = {
          ...(device && typeof device === "object" ? device : {}),
          ...sc,
        };

        // Derive Neptune target from site-control (+ list row fallbacks).
        let proxyAddr = deviceLive?.ProxyAddr ?? deviceLive?.proxy_addr;
        let securePort = deviceLive?.SecurePort ?? deviceLive?.secure_port;
        const link = deviceLive?.Link ?? deviceLive?.link;
        if (link && typeof link === "string") {
          try {
            const parsed = new URL(link);
            proxyAddr = parsed.hostname || proxyAddr;
            securePort = parsed.port ? Number(parsed.port) : securePort;
          } catch (e) {
            // Ignore bad link format; keep ProxyAddr/SecurePort from merged payload.
          }
        }
        if (!proxyAddr || String(proxyAddr).trim() === "") {
          openErrorNotification("Failed to load access items.");
          setAccessItemModalOptions([]);
          return [];
        }

        const stRaw = deviceLive?.SiteType ?? deviceLive?.site_type;
        const cloudSite = isCloudSiteType(stRaw);
        const neptuneUrl = buildNeptuneAccessOptionsUrl(
          proxyAddr,
          securePort,
          cloudSite,
        );
        if (!neptuneUrl) {
          openErrorNotification("Failed to load access items.");
          setAccessItemModalOptions([]);
          return [];
        }
        const headers = {
          "Content-Type": "application/json",
        };
        const siteNo = deviceLive?.SiteNo ?? deviceLive?.site_no;
        if (cloudSite && siteNo != null && siteNo !== "") {
          headers["x-siteno"] = String(siteNo);
        }
        const body = { AccessType: at };
        const stNum = Number(stRaw);
        if (Number.isFinite(stNum)) {
          body.SiteType = stNum;
        }

        await new Promise((r) => setTimeout(r, 300));

        const neptuneResult = await postNeptuneDevicesOptionsWithRetry(
          neptuneUrl,
          body,
          headers,
        );
        if (!neptuneResult.ok) {
          openErrorNotification("Failed to load access items.");
          setAccessItemModalOptions([]);
          return [];
        }

        const res = neptuneResult.response;
        const payload = res?.data;

        if (res.status >= 400) {
          openErrorNotification("Failed to load access items.");
          setAccessItemModalOptions([]);
          return [];
        }

        if (
          payload &&
          typeof payload === "object" &&
          "result" in payload &&
          payload.result !== 0
        ) {
          openErrorNotification("Failed to load access items.");
          setAccessItemModalOptions([]);
          return [];
        }

        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
        const mapped = list.map((row) => ({
          label: row.label != null ? String(row.label) : "",
          value: row.value,
        }));
        setAccessItemModalOptions(mapped);
        return mapped;
      } finally {
        setAccessItemModalOptionsLoading(false);
      }
    },
    [data, deviceOptionsData],
  );

  const openAccessItemModal = async () => {
    const deviceNo = form.getFieldValue("DeviceNo");
    if (deviceNo == null || deviceNo === "") {
      openErrorNotification("Please select a device first.");
      return;
    }
    const accessType = form.getFieldValue("AccessType");
    const itemNo = form.getFieldValue("ItemNo");
    const modalAccessType =
      accessType !== undefined && accessType !== null ? Number(accessType) : 2;
    accessTypeBeforeAccessItemModalRef.current = modalAccessType;
    accessItemModalForm.setFieldsValue({
      ModalAccessType: modalAccessType,
      ModalAccessItem: undefined,
    });
    setShowAccessItemModal(true);

    const options = await fetchAccessItemOptions(modalAccessType, deviceNo);
    if (
      itemNo !== undefined &&
      itemNo !== null &&
      itemNo !== "" &&
      options.length > 0
    ) {
      const match = options.find(
        (o) =>
          String(o.value) === String(itemNo) ||
          Number(o.value) === Number(itemNo),
      );
      if (match) {
        accessItemModalForm.setFieldsValue({ ModalAccessItem: match.value });
      }
    }
  };

  const handleAccessItemModalApply = () => {
    const v = accessItemModalForm.getFieldsValue();
    const nextType = v.ModalAccessType;
    const nextItem = v.ModalAccessItem;

    const selectedOption =
      nextItem !== undefined && nextItem !== null && nextItem !== ""
        ? accessItemModalOptions.find(
            (o) =>
              String(o.value) === String(nextItem) ||
              Number(o.value) === Number(nextItem),
          )
        : undefined;

    const itemName = selectedOption ? selectedOption.label : undefined;

    form.setFieldsValue({
      AccessType:
        nextType !== undefined && nextType !== null
          ? Number(nextType)
          : undefined,
      ItemNo:
        nextItem !== undefined && nextItem !== null && nextItem !== ""
          ? String(nextItem)
          : "",
      ItemName: itemName,
    });
    setShowAccessItemModal(false);
    setAccessItemModalOptions([]);
  };

  const openMapModal = () => {
    const lat = Number(form.getFieldValue("Latitude") ?? 0);
    const lng = Number(form.getFieldValue("Longitude") ?? 0);
    const coords = resolvePlateMapLocation(lat, lng);
    setMapPickerInitial(coords);
    setMapPickerCoords(coords);
    setShowMapModal(true);
  };

  const handleMapPickerLocationChange = (lat, lng) => {
    setMapPickerCoords({ lat, lng });
  };

  const handleMapModalApply = () => {
    const { lat, lng } = resolvePlateMapLocation(
      mapPickerCoords.lat,
      mapPickerCoords.lng,
    );
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      openErrorNotification("Latitude must be between -90.0 and 90.0");
      return;
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      openErrorNotification("Longitude must be between -180.0 and 180.0");
      return;
    }
    form.setFieldsValue({
      Latitude: roundCoord(lat),
      Longitude: roundCoord(lng),
    });
    setShowMapModal(false);
  };

  const handleMapModalClose = () => {
    setShowMapModal(false);
  };

  const handleAccessItemModalClose = () => {
    const previous = accessTypeBeforeAccessItemModalRef.current;
    form.setFieldsValue({
      AccessType:
        previous !== undefined && previous !== null
          ? Number(previous)
          : undefined,
    });
    setShowAccessItemModal(false);
    setAccessItemModalOptions([]);
  };

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
        initialValues={
          !id
            ? {
                AccessType: 2,
                DirectoryMode: 1,
                Location: 0,
                Latitude: 0,
                Longitude: 0,
              }
            : undefined
        }
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
          <div className="bg-white overflow-hidden border rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <RiAlignItemBottomFill className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                Virtual QR/NFC
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Name"
                    name="PlateName"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Description"
                    name="PlateDesc"
                    disabled={type === "info"}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Code"
                    name="PlateCode"
                    disabled={type === "info"}
                    loading={loading}
                    required
                  />
                </Col>
                {type === "info" ? (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                    <div className="flex w-full min-w-0 items-end gap-2">
                      <div className="min-w-0 flex-1">
                        <CustomInput
                          label="URL"
                          name="PlateUrl"
                          disabled
                          loading={loading}
                        />
                      </div>
                      <div className="w-[40px] shrink-0">
                        <Button
                          type="primary"
                          block
                          icon={<RiQrCodeFill className="text-[18px]" />}
                          className="!h-[32px] min-h-[32px] max-h-[32px] flex items-center justify-center"
                          disabled={!watchedPlateUrl}
                          onClick={() => setShowQrModal(true)}
                        />
                      </div>
                    </div>
                  </Col>
                ) : null}
                {!hideOemDealerForDealerAccount ? (
                  <>
                    {hideOemForOemAccount ? null : (
                      <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                        <CustomSelect
                          label="OEM"
                          name="OemNo"
                          options={modifiedOems}
                          disabled={type === "info"}
                          required
                          placeholder="Please select"
                          onChange={(value) => {
                            setSelectedOemNo(value || null);
                            setSelectedDealerNo(null);
                            form.setFieldsValue({
                              OemNo: value || undefined,
                              DealerNo: undefined,
                              DeviceNo: undefined,
                            });
                          }}
                          loading={loading}
                        />
                      </Col>
                    )}
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                      <CustomSelect
                        label="Dealer"
                        name="DealerNo"
                        options={modifiedDealers}
                        disabled={
                          type === "info" ||
                          (!hideOemForOemAccount &&
                            !(selectedOemNo || form.getFieldValue("OemNo")))
                        }
                        required
                        placeholder="Please select"
                        onChange={(value) => {
                          setSelectedDealerNo(value || null);
                          form.setFieldsValue({
                            DealerNo: value || undefined,
                            DeviceNo: undefined,
                          });
                        }}
                        formItemProps={{
                          rules: [
                            { required: true, message: "Dealer is required" },
                          ],
                        }}
                        loading={loading}
                      />
                    </Col>
                  </>
                ) : null}
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomSelect
                    label="Device"
                    name="DeviceNo"
                    options={modifiedDevices}
                    disabled={
                      type === "info" ||
                      !(selectedDealerNo || form.getFieldValue("DealerNo"))
                    }
                    required
                    placeholder="Please select"
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomSelect
                    label="Access Type"
                    name="AccessType"
                    options={plateAccessTypeOptions}
                    disabled={type === "info"}
                    required
                    placeholder="Please select"
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <div className="flex w-full min-w-0 items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <CustomInput
                        label="Access Item Name"
                        name="ItemName"
                        disabled
                        loading={loading}
                        required
                      />
                    </div>
                    <Form.Item name="ItemNo" hidden>
                      <input type="hidden" />
                    </Form.Item>
                    <div className="w-[118px] shrink-0">
                      <Button
                        type="primary"
                        block
                        className="!h-[32px] min-h-[32px] max-h-[32px] px-2 font-medium"
                        disabled={type === "info" || !hasDeviceForAccessItem}
                        onClick={openAccessItemModal}
                      >
                        Access Item
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomSelect
                    label="Directory Mode"
                    name="DirectoryMode"
                    options={plateDirectoryModeOptions}
                    disabled={type === "info"}
                    required
                    placeholder="Please select"
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <div className="flex w-full min-w-0 items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <CustomSelect
                        label="Location"
                        name="Location"
                        options={booleanOptions}
                        disabled={type === "info"}
                        required
                        placeholder="Please select"
                        loading={loading}
                        onChange={(value) => {
                          if (Number(value) !== 1) {
                            form.setFieldsValue({
                              Latitude: 0,
                              Longitude: 0,
                            });
                          }
                        }}
                      />
                    </div>
                    {locationEnabled ? (
                      <div className="w-[118px] shrink-0">
                        <Button
                          type="primary"
                          block
                          className="!h-[32px] min-h-[32px] max-h-[32px] px-2 font-medium"
                          disabled={type === "info"}
                          onClick={openMapModal}
                        >
                          Map
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </Col>
                {locationEnabled ? (
                  <>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                      <CustomInput
                        label="Latitude"
                        name="Latitude"
                        disabled={type === "info"}
                        loading={loading}
                        required
                        rules={[
                          { required: true, message: "Latitude is required" },
                          {
                            validator: (_, value) => {
                              if (value === "" || value == null) {
                                return Promise.resolve();
                              }
                              const num = Number(value);
                              if (!Number.isFinite(num)) {
                                return Promise.reject(
                                  new Error("Latitude must be a number"),
                                );
                              }
                              if (num < -90 || num > 90) {
                                return Promise.reject(
                                  new Error(
                                    "Latitude must be between -90.0 and 90.0",
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                      <CustomInput
                        label="Longitude"
                        name="Longitude"
                        disabled={type === "info"}
                        loading={loading}
                        required
                        rules={[
                          { required: true, message: "Longitude is required" },
                          {
                            validator: (_, value) => {
                              if (value === "" || value == null) {
                                return Promise.resolve();
                              }
                              const num = Number(value);
                              if (!Number.isFinite(num)) {
                                return Promise.reject(
                                  new Error("Longitude must be a number"),
                                );
                              }
                              if (num < -180 || num > 180) {
                                return Promise.reject(
                                  new Error(
                                    "Longitude must be between -180.0 and 180.0",
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Col>
                  </>
                ) : null}
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />

      <div className="container-fluid mx-auto">
        <Modal
          open={showMapModal}
          closable={false}
          onCancel={handleMapModalClose}
          footer={null}
          width={720}
          centered
          destroyOnClose
          title={
            <div className="flex items-center bg-gray-200 p-2 rounded-md">
              <RiAlignItemBottomFill className="text-[18px] text-black " />
              <span className="text-[14px] font-[Poppins] text-black  ml-2">
                Map
              </span>
            </div>
          }
        >
          {showMapModal ? (
            <div className="px-2 pt-0 pb-3">
              <PlateMapPicker
                latitude={mapPickerInitial.lat}
                longitude={mapPickerInitial.lng}
                onLocationChange={handleMapPickerLocationChange}
              />
            </div>
          ) : null}

          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              type="primary"
              className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
              onClick={handleMapModalApply}
            >
              <CheckCircleOutlined className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button className="reset-button" onClick={handleMapModalClose}>
              <CloseOutlined />
              <span>Cancel</span>
            </Button>
          </div>
        </Modal>

        <Modal
          open={showQrModal}
          closable={false}
          onCancel={() => setShowQrModal(false)}
          footer={null}
          width={420}
          centered
          destroyOnClose
          title={
            <div className="flex items-center bg-gray-200 p-2 rounded-md">
              <RiQrCodeFill className="text-[18px] text-black " />
              <span className="text-[14px] font-[Poppins] text-black  ml-2">
                QR Code
              </span>
            </div>
          }
        >
          <div className="flex flex-col items-center px-2 pt-2 pb-3">
            {watchedPlateUrl ? (
              <QRCode value={String(watchedPlateUrl)} size={220} />
            ) : null}
            <a
              href={String(watchedPlateUrl || "")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 text-[12px] text-center break-all text-blue-600 underline"
            >
              {watchedPlateUrl}
            </a>
          </div>

          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              className="reset-button"
              onClick={() => setShowQrModal(false)}
            >
              <CloseOutlined />
              <span>Cancel</span>
            </Button>
          </div>
        </Modal>

        <Modal
          open={showAccessItemModal}
          closable={false}
          onCancel={handleAccessItemModalClose}
          footer={null}
          width={580}
          centered
          destroyOnClose
          title={
            <div className="flex items-center bg-gray-200 p-2 rounded-md">
              <RiAlignItemBottomFill className="text-[18px] text-black " />
              <span className="text-[14px] font-[Poppins] text-black  ml-2">
                Access Item
              </span>
            </div>
          }
        >
          <Form
            form={accessItemModalForm}
            layout="vertical"
            validateTrigger={["onChange"]}
            autoComplete="off"
          >
            <div className="px-2 pt-0 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="Access Type"
                    name="ModalAccessType"
                    options={plateAccessTypeOptions}
                    required
                    placeholder="Please select"
                    onChange={(value) => {
                      const num =
                        value !== undefined && value !== null
                          ? Number(value)
                          : undefined;
                      form.setFieldsValue({
                        AccessType: num,
                      });
                      accessItemModalForm.setFieldsValue({
                        ModalAccessItem: undefined,
                      });
                      const deviceNo = form.getFieldValue("DeviceNo");
                      if (
                        num != null &&
                        Number.isFinite(num) &&
                        deviceNo != null &&
                        deviceNo !== ""
                      ) {
                        void fetchAccessItemOptions(num, deviceNo);
                      } else {
                        setAccessItemModalOptions([]);
                      }
                    }}
                    loading={false}
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="Access Item"
                    name="ModalAccessItem"
                    options={accessItemModalOptions}
                    placeholder="Please select"
                    allowClear
                    loading={accessItemModalOptionsLoading}
                  />
                </Col>
              </Row>
            </div>
          </Form>

          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              type="primary"
              className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
              onClick={handleAccessItemModalApply}
            >
              <CheckCircleOutlined className="!text-[14px] font-medium" />
              <span>Apply</span>
            </Button>
            <Button
              className="reset-button"
              onClick={handleAccessItemModalClose}
            >
              <CloseOutlined />
              <span>Cancel</span>
            </Button>
          </div>
        </Modal>
      </div>
    </>
  );
};
